import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Undo2, Copy } from 'lucide-react';
import { getApiUrl } from '../../utils/api';

const API_URL = getApiUrl();

// V81 — replay the refund cascade for refunds that landed before the cascade existed:
// lead → Refunded, doctor commission reversed, renewal queue closed, LMS told to
// revoke every batch/course and suspend the learner.
const RefundCascadeRepairCard = () => {
  const [running, setRunning] = useState(null);
  const [result, setResult] = useState(null);
  const [pullFromRazorpay, setPullFromRazorpay] = useState(false);

  const run = async (apply) => {
    if (apply && !window.confirm('Apply the refund cascade?\n\nFor every FULLY refunded enrolment this marks the lead Refunded, reverses the doctor commission, closes the renewal row and asks LMS to revoke all batches/courses. Safe to re-run.')) return;
    setRunning(apply ? 'apply' : 'dry');
    try {
      const res = await axios.post(
        `${API_URL}/api/data-repair/refund-cascade`,
        { apply, pull_from_razorpay: apply && pullFromRazorpay },
        { withCredentials: true, timeout: 180000 });
      setResult(res.data);
      toast[apply ? 'success' : 'info'](`${apply ? 'Applied to' : 'Dry-run · found'} ${res.data.count} refund(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Refund cascade repair failed');
    } finally {
      setRunning(null);
    }
  };

  const copyForLms = () => {
    const rows = (result?.affected || []).map((r) => [
      r.email, r.name, r.payment_id, `${r.currency} ${r.amount}`,
      r.fully_refunded ? 'FULL refund — revoke all access' : 'partial refund — keep access',
      r.refunded_at,
    ].join(' | '));
    navigator.clipboard.writeText(['email | name | payment | amount | action | refunded at', ...rows].join('\n'));
    toast.success('List copied — paste it to the LMS team');
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3" data-testid="refund-cascade-repair-card">
      <div className="flex items-center gap-2">
        <Undo2 className="w-4 h-4 text-orange-600" />
        <h3 className="font-semibold text-slate-900">Refund cascade (lead status, commissions, LMS access)</h3>
      </div>
      <p className="text-xs text-slate-500">
        Older refunds only updated the payment row — the lead stayed Enrolled, the doctor commission stayed
        payable and LMS was never asked to pull the batches. This replays the full cascade for every refund
        already in OPS. Run the dry-run first.
      </p>
      <label className="flex items-center gap-2 text-xs text-slate-600">
        <input type="checkbox" checked={pullFromRazorpay} onChange={(e) => setPullFromRazorpay(e.target.checked)}
               data-testid="refund-cascade-pull-toggle" />
        Re-pull the last 180 days of refunds from Razorpay first
      </label>
      <div className="flex gap-2">
        <button onClick={() => run(false)} disabled={!!running} className="btn-secondary text-sm" data-testid="refund-cascade-dryrun-btn">{running === 'dry' ? 'Checking…' : 'Dry-run'}</button>
        <button onClick={() => run(true)} disabled={!!running} className="btn-primary text-sm" data-testid="refund-cascade-apply-btn">{running === 'apply' ? 'Applying…' : 'Apply cascade'}</button>
        {result?.count > 0 && (
          <button onClick={copyForLms} className="btn-secondary text-sm inline-flex items-center gap-1" data-testid="refund-cascade-copy-btn"><Copy className="w-3.5 h-3.5" /> Copy list for LMS</button>
        )}
      </div>
      {result && (
        <div className="text-xs space-y-1" data-testid="refund-cascade-result">
          <p className="font-medium text-slate-700">
            {result.applied ? 'Applied to' : 'Would change'} {result.count} refund(s) · {result.refunded_payments_scanned} refunded payment(s) scanned
          </p>
          {(result.affected || []).map((r) => (
            <div key={r.payment_id} className="border border-slate-100 rounded-md p-2">
              <p className="text-slate-800">{r.name || '—'} · {r.email || 'no email'} · {r.currency} {r.amount} · {r.fully_refunded ? 'full' : 'partial'}</p>
              <p className="text-slate-500">{(r.changes || []).join(' · ')}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RefundCascadeRepairCard;
